import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase, Profile, UserRoleData, UserRole } from "@/lib/supabase";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRole: UserRole | null;
  userRoleData: UserRoleData | null;
  isLoading: boolean;
  isProfileComplete: boolean;
  needsProfileRedirect: boolean;
  isDeactivated: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRoleData, setUserRoleData] = useState<UserRoleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsProfileRedirect, setNeedsProfileRedirect] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);
  const initialLoadDone = useRef(false);

  const fetchProfile = useCallback(async (userId: string, userEmail?: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (!error && data) {
      if (data.is_deactivated) {
        setIsDeactivated(true);
        await supabase.auth.signOut();
        return null;
      }
      setProfile(data as Profile);
      return data as Profile;
    }
    
    // Profile doesn't exist - try to auto-repair for orphan accounts
    if (!data && !error && userEmail) {
      console.log("Auto-repair: Profile not found, attempting to create for user:", userId);
      
      const isChatterRegistration = sessionStorage.getItem("is_chatter_registration") === "true";
      const isRecruiterRegistration = sessionStorage.getItem("is_recruiter_registration") === "true";
      const affiliateProfileId = sessionStorage.getItem("affiliate_profile_id");
      
      try {
        const username = userEmail.split('@')[0];
        const { data: newProfile, error: profileError } = await supabase
          .from("profiles")
          .insert({
            user_id: userId,
            email: userEmail,
            username: username,
            is_profile_complete: false,
            referred_by_profile_id: affiliateProfileId || null,
          })
          .select()
          .single();
        
        if (profileError) {
          console.error("Auto-repair: Failed to create profile:", profileError);
          await supabase.auth.signOut();
          return null;
        }
        
        if (isChatterRegistration) {
          const { error: roleError } = await supabase.rpc('set_employee_role', {
            _user_id: userId,
            _employee_type: 'chatter'
          });
          if (roleError) console.error("Auto-repair: Failed to set chatter role:", roleError);
        } else if (isRecruiterRegistration) {
          const { error: roleError } = await supabase.rpc('set_employee_role', {
            _user_id: userId,
            _employee_type: 'recrutador'
          });
          if (roleError) console.error("Auto-repair: Failed to set recruiter role:", roleError);
        } else if (sessionStorage.getItem("is_va_postagem_registration") === "true") {
          const { error: roleError } = await supabase.rpc('set_employee_role', {
            _user_id: userId,
            _employee_type: 'va_postagem'
          });
          if (roleError) console.error("Auto-repair: Failed to set va_postagem role:", roleError);
        } else if (sessionStorage.getItem("is_ai_creator_registration") === "true") {
          const { error: roleError } = await supabase.rpc('set_employee_role', {
            _user_id: userId,
            _employee_type: 'ai_creator'
          });
          if (roleError) console.error("Auto-repair: Failed to set ai_creator role:", roleError);
        }
        
        setProfile(newProfile as Profile);
        setNeedsProfileRedirect(true);
        return newProfile as Profile;
      } catch (repairError) {
        console.error("Auto-repair: Unexpected error:", repairError);
        await supabase.auth.signOut();
        return null;
      }
    }
    
    return null;
  }, []);

  const fetchUserRole = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (!error && data) {
      setUserRoleData(data as UserRoleData);
    }
  }, []);

  const refreshProfile = async () => {
    if (user) {
      await Promise.all([fetchProfile(user.id), fetchUserRole(user.id)]);
    }
  };

  useEffect(() => {
    let mounted = true;
    let profileChannel: ReturnType<typeof supabase.channel> | null = null;
    let heartbeatInterval: NodeJS.Timeout | null = null;

    // Heartbeat - only sign out if we get a definitive auth error, not network errors
    const startHeartbeat = () => {
      heartbeatInterval = setInterval(async () => {
        try {
          const { error } = await supabase.auth.getUser();
          if (error && error.message?.includes("session_not_found")) {
            await supabase.auth.signOut();
          }
        } catch {
          // Network error - do NOT sign out, just ignore
        }
      }, 30000); // 30 seconds instead of 15
    };

    const loadUserData = async (userId: string, userEmail?: string) => {
      const profileData = await fetchProfile(userId, userEmail);
      await fetchUserRole(userId);
      
      if (profileData) {
        profileChannel = supabase
          .channel(`profile-status-${userId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'profiles',
              filter: `user_id=eq.${userId}`,
            },
            async (payload) => {
              if (payload.eventType === 'DELETE') {
                setIsDeactivated(true);
                await supabase.auth.signOut();
              } else if (payload.eventType === 'UPDATE') {
                const newData = payload.new as { is_deactivated?: boolean };
                if (newData.is_deactivated === true) {
                  setIsDeactivated(true);
                  await supabase.auth.signOut();
                }
              }
            }
          )
          .subscribe();
      }
      startHeartbeat();
      
      if (mounted) {
        setIsLoading(false);
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);

        if (!session?.user) {
          setProfile(null);
          setUserRoleData(null);
          if (initialLoadDone.current) {
            setIsLoading(false);
          }
        }
        // Don't reload data here on initial load - let getSession handle it
        // Only handle SIGN_IN/SIGN_OUT/TOKEN_REFRESHED events after initial load
        if (initialLoadDone.current && session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          setTimeout(() => loadUserData(session.user.id, session.user.email), 0);
        }
      }
    );

    // Initial load
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;

        if (session?.user) {
          // Validate token with server
          const { data: { user: validatedUser }, error } = await supabase.auth.getUser();
          
          if (error || !validatedUser) {
            await supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setIsLoading(false);
            return;
          }
          
          setSession(session);
          setUser(session.user);
          await loadUserData(session.user.id, session.user.email);
        } else {
          setSession(null);
          setUser(null);
          setIsLoading(false);
        }
      } catch {
        if (mounted) setIsLoading(false);
      } finally {
        initialLoadDone.current = true;
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (profileChannel) {
        supabase.removeChannel(profileChannel);
      }
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [fetchProfile, fetchUserRole]);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      return { error: error as Error | null };
    }

    // Check if profile is deactivated
    if (data?.user) {
      const profileData = await fetchProfile(data.user.id);
      if (profileData?.is_deactivated) {
        await supabase.auth.signOut();
        return { 
          error: new Error("Sua conta foi desativada. Entre em contato com a administração para mais informações.") 
        };
      }

      // Hard block for removed roles: Co-Admin, Chatter, Supervisor Chatter
      const { data: roleRow } = await supabase
        .from("user_roles")
        .select("role, employee_type")
        .eq("user_id", data.user.id)
        .maybeSingle();

      const blockedTypes = ["co_admin", "chatter", "supervisor_chatter"];
      if (
        roleRow?.role === "employee" &&
        roleRow?.employee_type &&
        blockedTypes.includes(roleRow.employee_type)
      ) {
        await supabase.auth.signOut();
        return {
          error: new Error("Esta função foi removida. Sua conta não está mais ativa."),
        };
      }
    }

    return { error: null };
  };


  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setUserRoleData(null);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    userRole: userRoleData?.role ?? null,
    userRoleData,
    isLoading,
    isProfileComplete: profile?.is_profile_complete ?? false,
    needsProfileRedirect,
    isDeactivated,
    signUp,
    signIn,
    signOut,
    refreshProfile,
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
